import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * CityReview – mobile‑first component (Welcome-style header, Montreal demo data)
 * - Minimal, frugal header (white/gray, glassy) to match your WelcomeScreen
 * - Search + category chips + review cards + FAB
 * - Drop-in: pure React + inline <style>
 */
const CityReview = ({ city = "Montreal", reviews: incoming = [] }) => {
  const navigate = useNavigate();

  // Category chips (extend as needed)
  const categories = [
    { key: "all", label: "All" },
    { key: "spot", label: "Sights" },
    { key: "food", label: "Food" },
    { key: "stay", label: "Stay" },
    { key: "life", label: "Life" },
    { key: "traffic", label: "Transit" },
  ];

  // Montreal-focused demo data if no props provided
  const demo = [
    {
      id: "mtl1",
      author: { name: "Arielle R.", role: "Local Guide", avatar: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQuUabt_p-1VL7nGBB_L3TGnqNHEmTPxf-HSw&s" },
      title: "Stunning at Notre‑Dame Basilica (Old Montréal)",
      content:
        "Arrive before 10am to beat the tour buses. The light show is magical, and the woodwork details are unreal. Grab a coffee on Rue Saint‑Sulpice after.",
      rating: 5,
      tags: ["Heritage", "Architecture", "Old Port"],
      photo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Bas%C3%ADlica_de_Notre-Dame%2C_Montreal%2C_Canad%C3%A1%2C_2017-08-11%2C_DD_26-28_HDR.jpg/1280px-Bas%C3%ADlica_de_Notre-Dame%2C_Montreal%2C_Canad%C3%A1%2C_2017-08-11%2C_DD_26-28_HDR.jpg",
      liked: 54,
      comments: 12,
      time: "2 days ago",
      type: "spot",
      district: "Old Montréal / Vieux‑Montréal",
    },
    {
      id: "mtl2",
      author: { name: "Marcus C.", role: "Foodie", avatar: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQuUabt_p-1VL7nGBB_L3TGnqNHEmTPxf-HSw&s" },
      title: "Schwartz's vs. Snowdon: smoked meat showdown",
      content:
        "Schwartz's brisket is juicier, but the line can be brutal. Tip: go mid‑afternoon. Snowdon Deli is less touristy and the dill pickles slap.",
      rating: 4,
      tags: ["Smoked Meat", "Deli", "Local Classic"],
      photo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS0P5NvuLcKkIEKA7v9Y4zOSkDVuwAdd1daXA&s",
      liked: 38,
      comments: 9,
      time: "3 days ago",
      type: "food",
      district: "The Main / Boulevard Saint‑Laurent",
    },
    {
      id: "mtl3",
      author: { name: "Léa B.", role: "Weekend Hiker", avatar: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQuUabt_p-1VL7nGBB_L3TGnqNHEmTPxf-HSw&s" },
      title: "Mount Royal lookout: best skyline for sunset",
      content:
        "15‑20 min walk from the bus stop; wear good shoes if it rained. Golden hour is gorgeous. After, descend to Mile End for bagels at St‑Viateur.",
      rating: 5,
      tags: ["Nature", "Viewpoint", "Bagels"],
      photo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQO7ijGEDhOcyLvx1k6yhaeR6LSCS8Or5uPvA&s",
      liked: 62,
      comments: 15,
      time: "5 days ago",
      type: "spot",
      district: "Parc du Mont‑Royal / Mile End",
    },
    {
      id: "mtl4",
      author: { name: "Samir H.", role: "Market Hopper", avatar: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQuUabt_p-1VL7nGBB_L3TGnqNHEmTPxf-HSw&s" },
      title: "Jean‑Talon Market tips (cheap berries, best tacos)",
      content:
        "Saturday morning is peak fresh. Buy pints of berries near closing for deals. Try the small taquería by the northeast entrance—al pastor is legit.",
      rating: 4,
      tags: ["Market", "Budget", "Street Food"],
      photo: "https://www.marchespublics-mtl.com/sites/default/files/styles/cropped_2000x935/public/market/19/39422ckellyjacob.jpg?itok=OMOq389N",
      liked: 29,
      comments: 6,
      time: "1 week ago",
      type: "life",
      district: "La Petite‑Italie / Villeray",
    },
    {
      id: "mtl5",
      author: { name: "Noah D.", role: "Daily Commuter", avatar: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQuUabt_p-1VL7nGBB_L3TGnqNHEmTPxf-HSw&s" },
      title: "STM basics: OPUS card + orange/green lines",
      content:
        "Buy an OPUS at any metro station. Most sights sit along the orange/green lines. Trains every ~4–6 min; late nights switch to buses—check schedules.",
      rating: 4,
      tags: ["Transit", "STM", "OPUS"],
      photo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTKVW7BvpeKgRgOsl_Qpo3i9uocNv2q1I4b4g&s",
      liked: 18,
      comments: 4,
      time: "6 days ago",
      type: "traffic",
      district: "Downtown / Plateau",
    },
  ];

  const [query, setQuery] = useState("");
  const [active, setActive] = useState("all");

  const list = useMemo(() => (incoming.length ? incoming : demo), [incoming]);

  const filtered = useMemo(() => {
    const q = query.trim();
    return list.filter((r) => {
      const inCat = active === "all" ? true : r.type === active;
      if (!q) return inCat;
      const hay = `${r.title} ${r.content} ${r.author?.name ?? ""} ${
        r.district ?? ""
      } ${r.tags?.join(" ")}`.toLowerCase();
      return inCat && hay.includes(q.toLowerCase());
    });
  }, [list, query, active]);

  const onShare = (id) => {
    if (navigator.share) {
      const item = list.find((x) => x.id === id);
      navigator.share({
        title: `${item?.title ?? "CityReview"}`,
        text: (item?.content || "").slice(0, 80) + "…",
      });
    } else {
      alert("Native share is not supported in this browser.");
    }
  };

  return (
    <>
      <style>{`
        .cr-shell { width: 100%; max-width: 430px; margin: 0 auto; background:#F7F7F8; min-height: 100vh; position: relative; }
        @media (min-width: 430px){ .cr-shell{ border-radius:40px; box-shadow:0 10px 40px rgba(0,0,0,.08); overflow:hidden; } }

        /* Welcome-style, frugal header */
        .cr-header { position: sticky; top: 0; z-index: 20; padding: 16px 16px 12px; color:#1a1a1a;
          background: rgba(255,255,255,0.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(0,0,0,0.06); }
        .cr-header .row{ display:flex; align-items:center; gap:10px; }
        .cr-back{ width:36px; height:36px; display:flex; align-items:center; justify-content:center; border-radius:10px; background:#F2F2F3; cursor:pointer; }
        .cr-title{ font-size:18px; font-weight:400; letter-spacing:.2px; }
        .cr-sub{ font-size:12px; color:#7a7a7a; margin-top:2px; }

        .cr-search { margin-top:12px; display:flex; align-items:center; gap:8px; padding:10px 12px; border-radius:10px; background:#fff; border:1px solid #E6E6E8; }
        .cr-search input{ flex:1; border:none; outline:none; background:transparent; font-size:14px; color:#1a1a1a; }
        .cr-search i{ color:#6a6a6a; }

        .cr-chipbar{ position: sticky; top: 76px; z-index: 15; background: #F7F7F8; padding: 10px 0 6px; overflow-x:auto; }
        .cr-chipwrap{ display:flex; gap:8px; padding:0 12px; }
        .cr-chip{ white-space:nowrap; padding:8px 12px; border-radius:999px; font-size:12px; border:1px solid #E7E7EA; color:#525252; background:#fff; cursor:pointer; }
        .cr-chip.active{ color:#1a1a1a; background:#1a1a1a0d; border-color:#DADAE0; font-weight:600; }

        .cr-list{ padding: 8px 12px 90px; }
        .cr-card{ background:#fff; border:1px solid #eee; border-radius:16px; padding:12px; margin:12px 0; box-shadow:0 2px 10px rgba(0,0,0,.03); }
        .cr-meta{ display:flex; align-items:center; gap:10px; }
        .cr-avatar{ width:36px; height:36px; border-radius:50%; object-fit:cover; background:#f1f1f1; }
        .cr-author{ font-size:13px; font-weight:600; color:#1a1a1a; }
        .cr-role{ font-size:11px; color:#8a8a8a; }
        .cr-title-txt{ font-size:15px; font-weight:700; margin:10px 0 6px; color:#151515; }
        .cr-time{ font-size:11px; color:#9a9a9a; margin-left:auto; }

        .cr-stars{ display:flex; gap:2px; }
        .cr-stars i{ font-size:14px; color:#FFC71A; }

        .cr-content{ font-size:13px; line-height:1.6; color:#5a5a5a; }
        .cr-tags{ display:flex; gap:8px; flex-wrap:wrap; margin:10px 0; }
        .cr-tag{ font-size:11px; padding:6px 8px; border-radius:6px; background:#F1F2F4; color:#6a6a6a; }

        .cr-photo{ width:100%; height:120px; border-radius:12px; object-fit:cover; margin-top:8px; background:#f3f3f3; }

        .cr-actions{ display:flex; align-items:center; gap:14px; color:#8a8a8a; font-size:12px; padding-top:8px; }
        .cr-actions .btn{ display:flex; align-items:center; gap:4px; cursor:pointer; }

        .cr-fab{ position: fixed; right: calc(50% - 430px/2 + 16px); bottom: 24px; z-index: 25; }
        @media (max-width: 430px){ .cr-fab{ right:16px; } }
        .cr-fab button{ border:none; background:#0F0F10; color:#fff; padding:14px 18px; border-radius:16px; font-weight:600; box-shadow:0 10px 24px rgba(0,0,0,.16); display:flex; align-items:center; gap:8px; }

        .empty{ padding:40px 12px; text-align:center; color:#8a8a8a; }
      `}</style>

      <div className="cr-shell">
        {/* Header */}
        <div className="cr-header">
          <div className="row">
            <div className="cr-back" onClick={() => navigate(-1)}>
              <i className="bi bi-chevron-left" />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div className="cr-title">City Reviews</div>
              <div className="cr-sub">Discover real reviews in {city}</div>
            </div>
          </div>
          <div className="cr-search">
            <i className="bi bi-search" />
            <input
              value={query}
              placeholder="Search reviews, spots, food…"
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <i className="bi bi-x-circle" style={{ cursor: "pointer" }} onClick={() => setQuery("")} />
            )}
          </div>
        </div>

        {/* Category chips */}
        <div className="cr-chipbar">
          <div className="cr-chipwrap">
            {categories.map((c) => (
              <button
                key={c.key}
                className={`cr-chip ${active === c.key ? "active" : ""}`}
                onClick={() => setActive(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Review list */}
        <div className="cr-list">
          {filtered.length === 0 && (
            <div className="empty">No results. Try another filter or keyword.</div>
          )}

          {filtered.map((r) => (
            <article key={r.id} className="cr-card">
              <div className="cr-meta">
                <img className="cr-avatar" src={r.author?.avatar} alt={r.author?.name} />
                <div>
                  <div className="cr-author">{r.author?.name} {r.author?.role && <span className="cr-role">· {r.author.role}</span>}</div>
                  <div className="cr-role">{r.district || ""}</div>
                </div>
                <div className="cr-time">{r.time}</div>
              </div>

              <div className="cr-title-txt">{r.title}</div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <div className="cr-stars">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className={`bi ${i < r.rating ? "bi-star-fill" : "bi-star"}`} />
                  ))}
                </div>
              </div>

              <div className="cr-content">{r.content}</div>

              {r.tags?.length > 0 && (
                <div className="cr-tags">
                  {r.tags.map((t, i) => (
                    <span className="cr-tag" key={i}>{t}</span>
                  ))}
                </div>
              )}

              {r.photo && <img className="cr-photo" src={r.photo} alt="photo" />}

              <div className="cr-actions">
                <div className="btn"><i className="bi bi-heart" />{r.liked}</div>
                <div className="btn"><i className="bi bi-chat" />{r.comments}</div>
                <div className="btn" style={{ marginLeft: "auto" }} onClick={() => onShare(r.id)}>
                  <i className="bi bi-share" /> Share
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Floating action button */}
        <div className="cr-fab">
          <button onClick={() => navigate("/write-review")}> <i className="bi bi-pen" /> Write a review</button>
        </div>
      </div>
    </>
  );
};

export default CityReview;
